/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ModuleDestinationSelect.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Combined component that includes both SelectModule and SelectDestination
    in a single reusable control. This simplifies forms that need to select
    both a module type and its destination.

 🔧 Notes:
    - Combines SelectModule and SelectDestination
    - Automatically handles the relationship between module and destination
    - Disables destination selection when no module is selected

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import SelectModule from './SelectModule';
import SelectDestination from './SelectDestination';

interface ModuleDestinationSelectProps {
  moduleValue: string;
  destinationValue: string;
  onModuleChange: (value: string) => void;
  onDestinationChange: (value: string) => void;
  modulePlaceholder?: string;
  destinationPlaceholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function ModuleDestinationSelect({
  moduleValue,
  destinationValue,
  onModuleChange,
  onDestinationChange,
  modulePlaceholder = 'Select Module',
  destinationPlaceholder = 'Select Destination',
  disabled = false,
  className = ''
}: ModuleDestinationSelectProps) {
  const handleModuleChange = (value: string) => {
    // Clear destination when module changes (only if it's a different module)
    if (value !== moduleValue) {
      onDestinationChange('');
    }
    // Always update the module value
    onModuleChange(value);
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <div className="flex-1">
        <SelectModule
          value={moduleValue || ''}
          onChange={handleModuleChange}
          placeholder={modulePlaceholder}
          disabled={disabled}
        />
      </div>
      <div className="flex-[2]">
        <SelectDestination
          moduleType={moduleValue || ''}
          value={destinationValue || ''}
          onChange={onDestinationChange}
          placeholder={destinationPlaceholder}
          disabled={disabled || !moduleValue}
        />
      </div>
    </div>
  );
}

